import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  InputAdornment
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ThumbUpAltOutlinedIcon from '@mui/icons-material/ThumbUpAltOutlined';
import ThumbUpAltIcon from '@mui/icons-material/ThumbUpAlt';
import { supabase } from '../../../lib/supabase';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../hooks/useAuth';
import ExplanationEditor from '../../../components/editor/ExplanationEditor';

const FreeBoardPage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [writeOpen, setWriteOpen] = useState(false);
  const [form, setForm] = useState({ author_name: '', title: '', content: '', password: '' });
  const [formError, setFormError] = useState('');
  const [likeLoading, setLikeLoading] = useState(null);
  const navigate = useNavigate();
  const { user, isLoggedIn, loading: authLoading } = useAuth();

  // 글 목록 불러오기
  const fetchPosts = async (searchValue = '') => {
    setLoading(true);
    let query = supabase
      .from('free_posts')
      .select('*')
      .order('created_at', { ascending: false });
    if (searchValue) {
      query = query.or(
        `title.ilike.%${searchValue}%,content.ilike.%${searchValue}%,author_name.ilike.%${searchValue}%`
      );
    }
    const { data, error } = await query;
    if (!error) setPosts(data);
    setLoading(false);
  };

  useEffect(() => {
    fetchPosts();
  }, []);

  // 검색
  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput);
    fetchPosts(searchInput);
  };

  // 글쓰기
  const handleWrite = async () => {
    setFormError('');
    if (!isLoggedIn && !form.author_name.trim()) {
      setFormError('작성자명을 입력하세요.');
      return;
    }
    if (!form.title.trim() || !form.content.trim()) {
      setFormError('제목, 내용을 모두 입력하세요.');
      return;
    }
    // 로그인/비로그인 구분
    const postData = {
      title: form.title,
      content: form.content,
      views: 0,
      likes: 0,
      is_guest: !isLoggedIn,
      author_name: isLoggedIn ? (user?.user_metadata?.full_name || user?.email || '회원') : form.author_name,
      user_id: isLoggedIn ? user?.id : null,
      password: !isLoggedIn ? (form.password || null) : null
    };
    const { error } = await supabase.from('free_posts').insert(postData);
    if (error) {
      setFormError('글 작성에 실패했습니다.');
    } else {
      setWriteOpen(false);
      setForm({ author_name: '', title: '', content: '', password: '' });
      fetchPosts(search);
    }
  };

  // 좋아요
  const handleLike = async (post) => {
    setLikeLoading(post.id);
    await supabase
      .from('free_posts')
      .update({ likes: (post.likes || 0) + 1 })
      .eq('id', post.id);
    fetchPosts(search);
    setLikeLoading(null);
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          자유게시판
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
          자유롭게 의견을 나누고 소통하세요.
        </Typography>
        {/* 검색창 + 글쓰기 버튼 */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <form onSubmit={handleSearch} style={{ flex: 1, marginRight: 16 }}>
            <TextField
              size="small"
              fullWidth
              placeholder="제목, 내용, 작성자 검색"
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton type="submit" size="small">
                      <SearchIcon />
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          </form>
          <Button variant="contained" onClick={() => setWriteOpen(true)}>
            글쓰기
          </Button>
        </Box>
        {/* 글 목록 테이블 */}
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>제목</TableCell>
              <TableCell>작성자</TableCell>
              <TableCell>작성일</TableCell>
              <TableCell align="center">좋아요</TableCell>
              <TableCell align="center">조회수</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {posts.map(post => (
              <TableRow key={post.id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/community/free-board/${post.id}`)}>
                <TableCell>{post.title}</TableCell>
                <TableCell>{post.author_name || '익명'}</TableCell>
                <TableCell>{new Date(post.created_at).toLocaleDateString()}</TableCell>
                <TableCell align="center" onClick={e => { e.stopPropagation(); handleLike(post); }}>
                  <IconButton size="small" disabled={likeLoading === post.id}>
                    {likeLoading === post.id ? <ThumbUpAltIcon color="primary" /> : <ThumbUpAltOutlinedIcon />}
                  </IconButton>
                  {post.likes || 0}
                </TableCell>
                <TableCell align="center">{post.views || 0}</TableCell>
              </TableRow>
            ))}
            {posts.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ color: 'text.secondary' }}>
                  게시글이 없습니다.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Box>
      {/* 글쓰기 다이얼로그 */}
      <Dialog open={writeOpen} onClose={() => setWriteOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>글쓰기</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          {authLoading ? (
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              로그인 상태 확인 중...
            </Typography>
          ) : (
            <Typography variant="subtitle2" color={isLoggedIn ? 'primary' : 'warning.main'} sx={{ mb: 1 }}>
              {isLoggedIn ? '현재 로그인 상태로 작성 중입니다.' : '현재 비로그인 상태로 작성 중입니다.'}
            </Typography>
          )}
          {console.log('useAuth:', { user, isLoggedIn, loading })}
          <TextField
            label="작성자명"
            value={isLoggedIn ? (user?.user_metadata?.full_name || user?.email || '회원') : form.author_name}
            onChange={e => {
              if (!isLoggedIn) setForm({ ...form, author_name: e.target.value });
            }}
            fullWidth
            required
            InputProps={{ readOnly: isLoggedIn }}
          />
          <TextField
            label="제목"
            value={form.title}
            onChange={e => setForm({ ...form, title: e.target.value })}
            fullWidth
            required
          />
          <ExplanationEditor
            value={form.content}
            onChange={val => setForm({ ...form, content: val })}
          />
          <TextField
            label="비밀번호 (선택, 비로그인 글 수정/삭제용)"
            value={form.password}
            onChange={e => setForm({ ...form, password: e.target.value })}
            fullWidth
            type="password"
            InputProps={{ readOnly: isLoggedIn }}
          />
          {formError && (
            <Typography color="error" variant="body2">{formError}</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWriteOpen(false)}>취소</Button>
          <Button variant="contained" onClick={handleWrite}>작성</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default FreeBoardPage; 